import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import Calendar from '../../Calendar';
import CalendarNew from '../../CalendarNew';

import useOnClickOutside from '../../../hooks/useOnClickOutside';
import classes from './styles.module.scss';

function convertToDateIfNeeded(value) {
  if (value == null || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  const date = new Date(value);
  // eslint-disable-next-line no-restricted-globals
  if (!isNaN(date.getTime())) {
    return date;
  }

  return value;
}

export default function DatePicker({
  name,
  value,
  setFieldValue,
  touched,
  error,
  setFieldTouched,
  dateFieldWidth,
  label,
  isPopupOpened,
  height,
  labelStyle,
  style,
  width,
  calendarPosition,
  onlyPastDates,
  warning,
  staticErrorPositon,
  readonly,
  newCalendar,
  calendarDefaultDate,
  note,
  clearable,
}) {
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [portalPosition, setPortalPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const calendarRef = useRef();
  const valueRef = useRef();
  const componentRef = useRef();

  const { t } = useTranslation();

  useOnClickOutside(
    calendarRef,
    () => setIsCalendarVisible(false),
    componentRef
  );

  useEffect(() => {
    if (calendarPosition) {
      return;
    }

    if (isCalendarVisible && !isPopupOpened && valueRef.current) {
      const coords = valueRef.current.getBoundingClientRect();
      setPortalPosition({
        top: coords.bottom + window.scrollY + 10,
        left: coords.left + window.scrollX,
        width: coords.width,
      });
    }
  }, [isCalendarVisible, isPopupOpened, calendarPosition]);

  const setValue = (date) => {
    setFieldValue?.(name, date);

    setTimeout(() => {
      setFieldTouched(name, true);
    });
  };

  const dateValue = convertToDateIfNeeded(value);

  // Close calendar on value change
  useEffect(() => {
    setIsCalendarVisible(false);
  }, [value]);

  return (
    <div
      className={classNames(classes.DateRange, {
        [classes.withFieldWidth]: !!dateFieldWidth,
        [classes.withCalendarPosition]: !!calendarPosition,
        [classes.staticErrorPositon]: staticErrorPositon,
        [classes.readonly]: readonly,
      })}
      ref={componentRef}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          setIsCalendarVisible(!isCalendarVisible);
        }
      }}
    >
      <div
        style={{ width: dateFieldWidth }}
        className={classNames(classes.dateContainer, {
          [classes.calendarOpen]: isCalendarVisible,
        })}
        onClick={() => setIsCalendarVisible(!isCalendarVisible)}
      >
        <div className={classes.label} style={labelStyle}>
          {label || 'Date'}
          {note && <span className={classes.note}>({note})</span>}
        </div>
        <div
          className={classes.value}
          ref={valueRef}
          style={{ ...style, height, width }}
        >
          {dateValue ? (
            moment(dateValue)?.format('DD MMMM YYYY')
          ) : (
            <span className={classes.placeholder}>
              {t('common.selectDate')}
            </span>
          )}
        </div>
      </div>
      {isCalendarVisible &&
        createPortal(
          <div
            className={classes.calendarPortal}
            style={{
              position: 'fixed',
              top: `${portalPosition.top}px`,
              left: `${portalPosition.left}px`,
              zIndex: 99999,
            }}
          >
            {newCalendar ? (
              <CalendarNew
                calendarRef={calendarRef}
                value={value === 'Invalid DateTime' ? new Date() : dateValue}
                setValue={setValue}
                onlyPastDates={onlyPastDates}
                setIsCalendarVisible={setIsCalendarVisible}
                calendarDefaultDate={calendarDefaultDate}
                clearable={clearable}
              />
            ) : (
              <Calendar
                calendarRef={calendarRef}
                value={value === 'Invalid DateTime' ? new Date() : dateValue}
                setValue={setValue}
                onlyPastDates={onlyPastDates}
              />
            )}
          </div>,
          document.body
        )}
      {touched && error && <div className={classes.error}>{error}</div>}
      {!error && warning && <div className={classes.warning}>{warning}</div>}
    </div>
  );
}
